import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Navigation } from "../components/Navigation";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Package, X as XIcon } from "lucide-react";
import { Lightbox } from "../components/prodotti/Lightbox";
import { OrderConfirmationModal } from "../components/prodotti/OrderConfirmationModal";
import { CategoryFilters } from "../components/prodotti/CategoryFilters";
import { ProductCard } from "../components/prodotti/ProductCard";
import { CartModal } from "../components/prodotti/CartModal";
import { formatPrice, formatPriceWithIva, findArticleDiscount } from "../components/prodotti/pricing";
import { isItalianDestination } from "../lib/italianGeoDetect";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

export const ProdottiPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [cart, setCart] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [wishlistIds, setWishlistIds] = useState([]); // Customer wishlist (only when logged in)
  const [wishlistMsg, setWishlistMsg] = useState("");
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [orderTelegramLink, setOrderTelegramLink] = useState("");
  const [orderConfirmationMessage, setOrderConfirmationMessage] = useState("");
  const [createAccount, setCreateAccount] = useState(false);
  const [accountPassword, setAccountPassword] = useState("");
  const [reviews, setReviews] = useState({});
  const [showReviewForm, setShowReviewForm] = useState({});
  const [reviewForm, setReviewForm] = useState({
    customer_name: "",
    customer_email: "",
    rating: 5,
    comment: ""
  });
  // Lightbox: stores {src, alt} when user clicks a product image to zoom
  const [lightbox, setLightbox] = useState(null);

  // ESC key closes the lightbox
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    // Prevent body scroll while open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox]);
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zipCode: "",
    notes: "",
    country: "italia",
    specificCountry: "" // Add field for specific country name (not shipping zone)
  });
  // State for article badges
  const [articleBadges, setArticleBadges] = useState({});
  // Public wishlist counts for social-proof badges ("X persone l'hanno desiderato")
  const [wishlistCounts, setWishlistCounts] = useState({});
  // Focused product (deep-link via ?article=ID, used for SEO head + scroll)
  const [articleFocusId, setArticleFocusId] = useState(null);
  
  // Initialize with empty array - shipping rules will be loaded from API
  const [shippingRules, setShippingRules] = useState([]);
  // Memoized: shipping rules excluding InPost (used in 3 select dropdowns)
  const shippingRulesNoInpost = useMemo(
    () => shippingRules.filter(r => r.id !== 'inpost'),
    [shippingRules]
  );

  // Coupon/Promo state
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [couponLoading, setCouponLoading] = useState(false);
  const [promoBanners, setPromoBanners] = useState([]);
  const [activePromotions, setActivePromotions] = useState([]);
  const [ratingsByArticle, setRatingsByArticle] = useState({});
  useEffect(() => {
    fetch(`${API}/reviews?status=approved`).then((r) => r.json()).then((d) => {
      const acc = {};
      (d.reviews || []).forEach((r) => {
        if (!r.article_id || !r.rating) return;
        acc[r.article_id] = acc[r.article_id] || { sum: 0, count: 0 };
        acc[r.article_id].sum += Number(r.rating);
        acc[r.article_id].count += 1;
      });
      setRatingsByArticle(acc);
    }).catch(() => {});
  }, []);
  // Track which emails we've already tried to auto-apply a welcome coupon for,
  // so we don't keep refetching when the user re-renders the cart.
  const [autoAppliedEmails] = useState(() => new Set());

  // Auto-apply the customer's unused welcome coupon (the BENVENUTO-XXXXXX
  // they received via email at registration) when they enter their email at
  // checkout. Saves them the "I forgot to paste the code" frustration.
  useEffect(() => {
    const email = (customerInfo.email || "").trim().toLowerCase();
    if (!email || !email.includes("@") || appliedCoupon || autoAppliedEmails.has(email) || cart.length === 0) {
      return;
    }
    let cancelled = false;
    (async () => {
      let attemptedCode = "";
      try {
        autoAppliedEmails.add(email);
        const { data } = await axios.get(`${API}/coupons/pending-welcome`, { params: { email } });
        if (cancelled || !data?.coupon?.code) return;
        attemptedCode = data.coupon.code;
        setCouponCode(attemptedCode);

        // Validate the coupon against the current cart so we get the exact
        // discount_amount back from the backend (handles applicable_articles).
        const validateResp = await axios.post(`${API}/coupons/validate`, {
          code: data.coupon.code,
          source: "auto",
          customer_email: email,
          items: cart.map(item => ({
            article_id: item.article_id || item.article?.id,
            quantity: item.quantity,
            price: (item.article?.price || 0) * (1 + (item.article?.iva_percentage || 0) / 100),
          })),
        });
        if (cancelled) return;
        setAppliedCoupon({ ...validateResp.data, validatedEmail: email });
        setCouponError("");
        setCouponCode(data.coupon.code);
        toast.success(`🎁 Coupon di benvenuto applicato! -${data.coupon.discount_percent}%`);
      } catch (err) {
        if (!cancelled && attemptedCode) {
          const detail = err?.response?.data?.detail;
          setCouponError(typeof detail === "string" ? detail : "Il codice non è stato applicato automaticamente. Premi Applica per riprovare.");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [customerInfo.email, cart.length, appliedCoupon, autoAppliedEmails]);

  useEffect(() => {
    if (appliedCoupon?.email_bound && appliedCoupon.validatedEmail !== (customerInfo.email || "").trim().toLowerCase()) {
      setAppliedCoupon(null);
      setCouponError("Email modificata: applica nuovamente il codice sconto.");
    }
  }, [customerInfo.email, appliedCoupon]);

  // Reset checkout state function
  const resetCheckoutState = () => {
    setIsCheckingOut(false);
    setShowPaymentOptions(false);
    setSelectedPaymentMethod("");
    setShowOrderConfirmation(false);
    setOrderConfirmationMessage("");
    setSelectedShippingMethod("standard"); // Reset to standard shipping
    setInpostPickupPoint(""); // Reset InPost pickup point
    setInpostInsurance(false); // Reset insurance
    // Keep the coupon while moving between cart/payment steps or reopening.
    // It is cleared only explicitly, on email change, or when the cart empties.
  };

  useEffect(() => {
    if (cart.length === 0) {
      setCouponCode("");
      setAppliedCoupon(null);
      setCouponError("");
    }
  }, [cart.length]);

  // InPost shipping states
  const [selectedShippingMethod, setSelectedShippingMethod] = useState("standard"); // "standard" or "inpost"
  const [inpostPickupPoint, setInpostPickupPoint] = useState(""); // Codice/indirizzo punto di ritiro
  const [inpostInsurance, setInpostInsurance] = useState(false); // Assicurazione facoltativa

  // Load shipping rules
  const loadShippingRules = async () => {
    try {
      const response = await fetch(`${API}/shipping-rules`);
      if (response.ok) {
        const data = await response.json();
        //console.log('Shipping rules loaded:', data);
        if (data.shipping_rules && Array.isArray(data.shipping_rules)) {
          setShippingRules(data.shipping_rules);
        }
      }
    } catch (error) {
      console.error('Errore caricamento regole spedizioni:', error);
    }
  };

  // Autofill checkout customer info from logged-in customer profile + load wishlist
  useEffect(() => {
    const token = sessionStorage.getItem('access_token');
    if (!token) return;
    axios.get(`${API}/customer/profile`).then((res) => {
      const p = res.data || {};
      if (p.role !== 'customer') return;
      setCustomerInfo((prev) => ({
        ...prev,
        name: prev.name || p.full_name || "",
        email: prev.email || p.email || "",
        phone: prev.phone || p.phone || "",
        address: prev.address || p.default_address || "",
        city: prev.city || p.default_city || "",
        zipCode: prev.zipCode || p.default_zip || "",
        // Map ISO-2 country code (IT/FR/DE/...) saved in user profile to the
        // full human-readable country name expected by the checkout free-text
        // field. Legacy values like "NAZIONALE" (a zone name, not a country)
        // would fail shipping validation, so we sanitize them too.
        specificCountry: prev.specificCountry || (() => {
          const code = (p.default_country || "").trim().toUpperCase();
          const map = {
            IT: "Italia", FR: "Francia", DE: "Germania", ES: "Spagna",
            PT: "Portogallo", AT: "Austria", BE: "Belgio", NL: "Paesi Bassi",
            LU: "Lussemburgo", IE: "Irlanda", PL: "Polonia", CZ: "Repubblica Ceca",
            SK: "Slovacchia", HU: "Ungheria", SI: "Slovenia", HR: "Croazia",
            RO: "Romania", BG: "Bulgaria", GR: "Grecia", DK: "Danimarca",
            SE: "Svezia", FI: "Finlandia", EE: "Estonia", LV: "Lettonia",
            LT: "Lituania", MT: "Malta", CY: "Cipro", GB: "Regno Unito",
            UK: "Regno Unito", CH: "Svizzera", NO: "Norvegia", US: "Stati Uniti",
            CA: "Canada",
          };
          if (map[code]) return map[code];
          // Plain text like "Italia", "Francia": keep as-is; "NAZIONALE" or
          // similar zone labels: drop so the user must re-enter a real country.
          const blacklist = ["NAZIONALE", "EUROPA", "EUROPEA", "INTERNATIONAL", "ESTERO"];
          if (blacklist.includes(code)) return "";
          return p.default_country || "";
        })(),
      }));
    }).catch(() => { /* not logged in or no profile */ });

    // Load wishlist IDs
    axios.get(`${API}/customer/wishlist/ids`).then((res) => {
      if (Array.isArray(res.data)) setWishlistIds(res.data);
    }).catch(() => { /* not logged in */ });
  }, []);

  // Toggle product in wishlist (only when logged in)
  const toggleWishlist = async (articleId) => {
    const token = sessionStorage.getItem('access_token');
    if (!token) {
      // Redirect to login with intent to return
      if (window.confirm("Per salvare i preferiti devi accedere al tuo account. Vuoi farlo ora?")) {
        window.location.href = '/login';
      }
      return;
    }
    const isInList = wishlistIds.includes(articleId);
    try {
      if (isInList) {
        await axios.delete(`${API}/customer/wishlist/${articleId}`);
        setWishlistIds((prev) => prev.filter((id) => id !== articleId));
        setWishlistMsg("Rimosso dai preferiti");
      } else {
        await axios.post(`${API}/customer/wishlist`, { article_id: articleId });
        setWishlistIds((prev) => [...prev, articleId]);
        setWishlistMsg("Aggiunto ai preferiti");
      }
      setTimeout(() => setWishlistMsg(""), 2000);
    } catch (e) {
      if (e.response?.status === 401) {
        window.location.href = '/login';
      }
    }
  };

  // Load promotional banners
  const loadPromoBanners = async () => {
    try {
      const response = await fetch(`${API}/active-banners`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setPromoBanners(data);
        }
      }
    } catch (error) {
      console.error('Errore caricamento banner:', error);
    }
  };

  // Load active promotions for product badges
  const loadActivePromotions = async () => {
    try {
      const response = await fetch(`${API}/active-promotions`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) {
          setActivePromotions(data);
        }
      }
    } catch (error) {
      console.error('Errore caricamento promozioni:', error);
    }
  };

  // Load public wishlist counts for social-proof badges on product cards
  const loadWishlistCounts = async () => {
    try {
      const response = await axios.get(`${API}/public/wishlist-counts`);
      if (response.data && typeof response.data === 'object') {
        setWishlistCounts(response.data);
      }
    } catch (error) {
      // silent: badge just won't show
    }
  };

  // Check if a product has an active discount (delegates to pure helper)
  const getArticleDiscount = (articleId) => findArticleDiscount(activePromotions, articleId);

  // Validate and apply coupon code
  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError("");
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim().toUpperCase(),
        customer_email: (customerInfo.email || "").trim().toLowerCase(),
        items: cart.map(item => ({
          article_id: item.article_id || item.article?.id,
          quantity: item.quantity,
          price: (item.article?.price || 0) * (1 + (item.article?.iva_percentage || 0) / 100)
        }))
      });
      setAppliedCoupon({ ...response.data, validatedEmail: (customerInfo.email || "").trim().toLowerCase() });
      setCouponError("");
    } catch (error) {
      setAppliedCoupon(null);
      setCouponError(error.response?.data?.detail || "Codice sconto non valido o scaduto");
    } finally {
      setCouponLoading(false);
    }
  };

  // Remove applied coupon
  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError("");
  };

  // Get discount amount
  const getDiscountAmount = () => {
    if (!appliedCoupon) return 0;
    return appliedCoupon.discount_amount || 0;
  };

  // Load reviews for an article
  const loadReviews = async (articleId) => {
    try {
      const response = await fetch(`${API}/reviews?article_id=${articleId}&status=approved`);
      if (response.ok) {
        const data = await response.json();
        setReviews(prev => ({
          ...prev,
          [articleId]: data.reviews || []
        }));
      }
    } catch (error) {
      console.error('Errore caricamento recensioni:', error);
    }
  };

  // Load badges for articles
  const loadArticleBadges = async (articleIds) => {
    try {
      const badgePromises = articleIds.map(id => 
        fetch(`${API}/articles/${id}/badges`).then(r => r.json()).catch(() => ({}))
      );
      const badgesData = await Promise.all(badgePromises);
      const badgesMap = {};
      articleIds.forEach((id, index) => {
        badgesMap[id] = badgesData[index];
      });
      setArticleBadges(badgesMap);
    } catch (error) {
      console.error('Errore caricamento badge:', error);
    }
  };

  // Track article view
  const trackArticleView = async (articleId) => {
    try {
      await axios.post(`${API}/articles/${articleId}/view`);
    } catch (error) {
      console.warn('Failed to track view:', error);
    }
  };

  // Submit review
  const submitReview = async (articleId) => {
    try {
      const response = await fetch(`${API}/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          article_id: articleId,
          customer_name: reviewForm.customer_name,
          customer_email: reviewForm.customer_email,
          rating: reviewForm.rating,
          comment: reviewForm.comment
        })
      });

      if (response.ok) {
        alert('✅ Recensione inviata! Sarà pubblicata dopo approvazione.');
        setShowReviewForm(prev => ({ ...prev, [articleId]: false }));
        setReviewForm({
          customer_name: "",
          customer_email: "",
          rating: 5,
          comment: ""
        });
      } else {
        alert('❌ Errore nell\'invio della recensione');
      }
    } catch (error) {
      console.error('Errore invio recensione:', error);
      alert('❌ Errore nell\'invio della recensione');
    }
  };

  useEffect(() => {
    fetchProdottiPubblici();
    loadShippingRules();
    loadPromoBanners();
    loadActivePromotions();
    loadWishlistCounts();
    // Load cart from localStorage
    try {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        const parsedCart = JSON.parse(savedCart);
        const validCart = Array.isArray(parsedCart)
          ? parsedCart.filter((item) => item && item.article && Number.isFinite(Number(item.article.price)))
          : [];
        setCart(validCart);
        if (validCart.length !== (Array.isArray(parsedCart) ? parsedCart.length : 0)) {
          localStorage.setItem('cart', JSON.stringify(validCart));
        }
      }
    } catch (error) {
      console.warn('Carrello locale non leggibile: reimpostato', error);
      try { localStorage.removeItem('cart'); } catch (_) { /* no-op */ }
      setCart([]);
    }
    
    // Check URL parameters for filter
    const urlParams = new URLSearchParams(window.location.search);
    const filterParam = urlParams.get('filter');
    if (filterParam) {
      setFiltroCategoria(filterParam);
    }
    // Article focus param (used by sitemap URLs /prodotti?article={id})
    const articleParam = urlParams.get('article');
    if (articleParam) {
      setArticleFocusId(articleParam);
    }
    // Auto-open cart modal when coming from a product detail page
    // (link uses /prodotti?openCart=1)
    if (urlParams.get('openCart') === '1') {
      setIsCartOpen(true);
      // Clean up the URL so refresh doesn't keep re-opening it
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, []);
  
  // Update customerInfo.country when shipping rules are loaded (only if not set yet)
  useEffect(() => {
    if (shippingRules.length > 0 && (customerInfo.country === "italia" || customerInfo.country === "")) {
      // Only set default if country is still the initial value
      // Find the "NAZIONALE" (Italy) rule
      const italyRule = shippingRules.find(rule => {
        const z = (rule.zone || "").toLowerCase();
        return z === 'national' || z === 'nazionale' || rule.name === 'NAZIONALE' || rule.id === 'italia';
      });
      if (italyRule) {
        setCustomerInfo(prev => {
          // Only update if still at default value
          if (prev.country === "italia" || prev.country === "") {
            //console.log('🇮🇹 Set default country to NAZIONALE:', italyRule.id);
            return { ...prev, country: italyRule.id };
          }
          return prev;
        });
      } else {
        // Fallback to first rule
        setCustomerInfo(prev => {
          if (prev.country === "italia" || prev.country === "") {
            //console.log('⚠️ NAZIONALE not found, using first rule:', shippingRules[0].id);
            return { ...prev, country: shippingRules[0].id };
          }
          return prev;
        });
      }
    }
  }, [shippingRules]);  // Removed customerInfo dependency to avoid re-running when user changes country

  // Pre-fill specificCountry from the selected shipping zone — ONLY for
  // single-country rules (Italia, Regno Unito, Brasile...). Multi-country
  // zones (Europa, Resto del Mondo, ecc.) must NOT pre-fill: doing so filled
  // the field with the ZONE name ("Europa"), the customer submitted it as-is
  // and the backend validator blocked every attempt (bug: Polish customer
  // blocked 8 times). Strips emoji prefix so we get "Italia" not "🇮🇹 Italia".
  const MULTI_COUNTRY_ZONES = ["europa", "europe", "resto del mondo", "internazionale", "international", "america", "mondo", "world"];
  const ZONE_LABEL_LEFTOVERS = [...MULTI_COUNTRY_ZONES, "nazionale", "national"];
  useEffect(() => {
    if (shippingRules.length === 0) return;
    const selectedRule = shippingRules.find(r => r.id === customerInfo.country);
    if (!selectedRule) return;
    setCustomerInfo(prev => {
      // Never pre-fill before the customer has actively chosen a destination:
      // the default "italia" rule used to pre-fill "Italia" for everyone, and a
      // French customer paid the Italian rate without noticing.
      if (!prev.countryChosen) return prev;
      let cleanName = (selectedRule.country || "").replace(/^[\p{Emoji_Presentation}\p{Extended_Pictographic}\s]+/u, "").trim();
      // The Italy rule is labelled "NAZIONALE" (a zone word, not a country):
      // pre-fill the actual country name instead.
      if (["nazionale", "national"].includes(cleanName.toLowerCase())) cleanName = "Italia";
      const isZone = MULTI_COUNTRY_ZONES.includes(cleanName.toLowerCase());
      const prevVal = (prev.specificCountry || "").trim().toLowerCase();
      // If a previous render (or old code) pre-filled the field with a zone
      // label ("Europa", "NAZIONALE"...), clear/replace it so the customer
      // ends up with a real country name.
      if (isZone) {
        if (ZONE_LABEL_LEFTOVERS.includes(prevVal)) {
          return { ...prev, specificCountry: "" };
        }
        return prev;
      }
      if (prev.specificCountry && prev.specificCountry.trim() && !ZONE_LABEL_LEFTOVERS.includes(prevVal)) return prev;
      if (!cleanName) return prev;
      return { ...prev, specificCountry: cleanName };
    });
  }, [shippingRules, customerInfo.country]);

  // Auto-detect Italian destination from city/CAP. When the customer types
  // a known Italian city ("Napoli") or a valid Italian CAP (5 digits in range)
  // we automatically:
  //   1. Switch the destination dropdown to the "national" rule (Italy)
  //   2. Pre-fill specificCountry with "Italia"
  // Skipped if the customer has already manually picked a non-national zone
  // (we never override an explicit choice).
  useEffect(() => {
    if (shippingRules.length === 0) return;
    if (!customerInfo.city && !customerInfo.zipCode) return;
    // Explicit country choice always wins: French CAPs (e.g. 03100 Montluçon)
    // overlap Italian ranges and used to force the destination back to Italy.
    if (customerInfo.countryChosen) return;
    if (!isItalianDestination({ city: customerInfo.city, zipCode: customerInfo.zipCode })) return;

    const currentRule = shippingRules.find(r => r.id === customerInfo.country);
    const italyRule = shippingRules.find(r => {
      const z = (r.zone || "").toLowerCase();
      return z === "national" || z === "nazionale" || r.id === "italia";
    });
    if (!italyRule) return;

    // Already pointing at Italy → nothing to do
    if (currentRule) {
      const z = (currentRule.zone || "").toLowerCase();
      if (z === "national" || z === "nazionale" || currentRule.id === "italia") return;
    }

    // The customer hasn't picked anything explicit yet (or is on Europa/etc.
    // by default because the page hadn't loaded the rules in time). Switch.
    setCustomerInfo(prev => {
      if (prev.country === italyRule.id) return prev;
      return {
        ...prev,
        country: italyRule.id,
        // Always overwrite specificCountry here — the city/CAP tell us
        // unambiguously the customer is in Italy
        specificCountry: "Italia",
      };
    });
  }, [customerInfo.city, customerInfo.zipCode, customerInfo.countryChosen, shippingRules]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cart));
    } catch (error) {
      console.warn('Impossibile salvare il carrello locale', error);
    }
    // Notify other components on the page (e.g. floating cart badges) that
    // the cart contents have changed.
    window.dispatchEvent(new Event("cart:updated"));
  }, [cart]);
  // Additional useEffect for enhanced functionality
  useEffect(() => {
    // This useEffect can be used for additional initialization logic
    //console.log('Additional useEffect initialized');
  }, []);

  // Auto-close order confirmation modal after 5 seconds
  useEffect(() => {
    if (showOrderConfirmation) {
      const timer = setTimeout(() => {
        setShowOrderConfirmation(false);
        setCart([]);
        setIsCartOpen(false);
        setShowPaymentOptions(false);
        setSelectedPaymentMethod("");
        setCustomerInfo({
          name: "", email: "", phone: "", address: "", city: "", zipCode: "", notes: "", country: "italia", specificCountry: ""
        });
        window.location.href = "/";
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showOrderConfirmation]);


  const fetchProdottiPubblici = async () => {
    try {
      // Don't set loading to true - show UI immediately
      // Usa l'endpoint pubblico per ottenere gli articoli
      const response = await axios.get(`${API}/public/articles`);
      const activeArticles = response.data.filter(article => article.is_active !== false);
      setArticles(activeArticles);
      
      // Load badges for all articles
      if (activeArticles.length > 0) {
        const articleIds = activeArticles.map(a => a.id);
        loadArticleBadges(articleIds);
      }
      
      setError("");
    } catch (err) {
      console.error("Error fetching products:", err);
      // Se non riusciamo a ottenere i prodotti, mostriamo un messaggio friendly
      setError("I prodotti verranno caricati a breve. Contattaci per informazioni!");
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (article) => {
    // Track cart add event
    axios.post(`${API}/articles/${article.id}/cart-add`).catch(err => {
      console.warn('Failed to track cart add:', err);
    });
    
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.article_id === article.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.article_id === article.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, { article_id: article.id, article: article, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (articleId) => {
    setCart(prevCart => prevCart.filter(item => item.article_id !== articleId));
  };

  const updateCartQuantity = (articleId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(articleId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.article_id === articleId
          ? { ...item, quantity: quantity }
          : item
      )
    );
  };

  const getCartTotal = () => {
    const itemsTotal = getCartItemsTotal();
    const shippingTotal = getShippingTotal();
    const discount = getDiscountAmount();
    return Math.max(0, itemsTotal - discount) + shippingTotal;
  };

  const getCartItemsTotal = () => {
    return cart.reduce((total, item) => {
      // Add safety checks
      if (!item || !item.article || typeof item.article.price !== 'number') {
        return total;
      }
      
      const price = parseFloat(item.article.price) || 0;
      const ivaRate = parseFloat(item.article.iva_percentage || 0) / 100;
      const quantity = parseInt(item.quantity) || 0;
      const priceWithIva = price * (1 + ivaRate);
      return total + (priceWithIva * quantity);
    }, 0);
  };

  const getShippingTotal = () => {
    if (cart.length === 0) return 0;
    
    const totalQuantity = cart.reduce((total, item) => total + parseInt(item.quantity || 1), 0);
    
    // Find selected shipping rule
    const selectedRule = shippingRules.find(rule => rule.id === customerInfo.country);
    
    // Weight-based shipping (extra-Europe: USA, Brazil, etc.)
    if (selectedRule && selectedRule.calculation_type === 'weight') {
      const totalWeight = cart.reduce((total, item) => {
        const itemWeight = parseFloat(item.article?.weight || 1);
        return total + (itemWeight * parseInt(item.quantity || 1));
      }, 0);
      const roundedWeight = Math.ceil(totalWeight);
      
      // Check for custom weight tiers (e.g. India: 1kg=40, 2kg=48, 3kg=60)
      if (selectedRule.weight_tiers && selectedRule.weight_tiers.length > 0) {
        const tiers = selectedRule.weight_tiers.sort((a, b) => a.kg - b.kg);
        for (let i = tiers.length - 1; i >= 0; i--) {
          if (roundedWeight >= tiers[i].kg) {
            if (i === tiers.length - 1) {
              // Beyond last tier: use extra_kg_rate if available
              const extraRate = selectedRule.extra_kg_rate || (i > 0 ? (tiers[i].cost - tiers[i-1].cost) : tiers[i].cost);
              return tiers[i].cost + ((roundedWeight - tiers[i].kg) * extraRate);
            }
            const extraKg = roundedWeight - tiers[i].kg;
            const ratePerKg = (tiers[i+1].cost - tiers[i].cost) / (tiers[i+1].kg - tiers[i].kg);
            return tiers[i].cost + (extraKg * ratePerKg);
          }
        }
        return tiers[0].cost;
      }
      
      // Default linear weight calculation
      const baseCost = selectedRule.base_cost || 40;
      const additionalCost = selectedRule.additional_cost || 6;
      if (roundedWeight <= 1) return baseCost;
      return baseCost + ((roundedWeight - 1) * additionalCost);
    }
    
    // Belinterpost shipping (Belarus & Russia) - Weight-based tiers
    if (selectedRule && (selectedRule.zone === 'belarus' || selectedRule.zone === 'russia')) {
      const weight = totalQuantity; // 1 item ≈ 1 kg
      
      if (weight <= 5) {
        return 48.00;
      } else if (weight <= 30) {
        return 48.00 + ((weight - 5) * 4.00);
      } else {
        return 148.00; // Max 30kg
      }
    }
    
    // UK & Netherlands - Fixed base + increment per items
    if (selectedRule && (selectedRule.zone === 'uk' || selectedRule.zone === 'netherlands')) {
      const baseQuantity = selectedRule.base_quantity || 4;
      const additionalQuantity = selectedRule.additional_quantity || 4;
      const baseCost = selectedRule.base_cost || 18.00;
      const additionalCost = selectedRule.additional_cost || 10.00;
      
      if (totalQuantity <= baseQuantity) {
        return baseCost;
      } else {
        const additionalGroups = Math.ceil((totalQuantity - baseQuantity) / additionalQuantity);
        return baseCost + (additionalGroups * additionalCost);
      }
    }
    
    // InPost shipping calculation
    if (selectedShippingMethod === "inpost") {
      const z = (selectedRule?.zone || "").toLowerCase();
      const isItaly = z === 'national' || z === 'nazionale' || selectedRule?.name === 'NAZIONALE' || selectedRule?.id === 'italia';
      
      // Base cost: €6 Italia, €8 Estero for 1-4 items
      let inpostCost = isItaly ? 6.00 : 8.00;
      
      // Calculate additional cost for multiples of 4 items
      if (totalQuantity > 4) {
        const additionalGroups = Math.ceil((totalQuantity - 4) / 4);
        const incrementCost = isItaly ? 5.00 : 8.00;
        inpostCost += additionalGroups * incrementCost;
      }
      
      // Add insurance if selected
      if (inpostInsurance) {
        inpostCost += 3.00;
      }
      
      return inpostCost;
    }
    
    // Standard shipping calculation (fallback for other countries)
    if (shippingRules.length === 0) return 0;
    
    // If selectedRule wasn't found above, find the fallback rule now
    if (!selectedRule) {
      const fallbackRule = shippingRules.find(rule => rule.id === customerInfo.country) || shippingRules[0];
      
      // Calculate shipping cost based on quantity (already have totalQuantity from line 4739)
      if (totalQuantity <= fallbackRule.base_quantity) {
        return fallbackRule.base_cost;
      }
      
      const additionalGroups = Math.ceil((totalQuantity - fallbackRule.base_quantity) / fallbackRule.additional_quantity);
      return fallbackRule.base_cost + (additionalGroups * fallbackRule.additional_cost);
    }
    
    // Use the already-found selectedRule
    if (totalQuantity <= selectedRule.base_quantity) {
      return selectedRule.base_cost;
    }
    
    const additionalGroups = Math.ceil((totalQuantity - selectedRule.base_quantity) / selectedRule.additional_quantity);
    return selectedRule.base_cost + (additionalGroups * selectedRule.additional_cost);
  };

  const getCartItemsCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  // Italian CAP ranges by province (matches backend shipping_validator.py).
  // 35170 (Bruz, France) deliberately falls OUTSIDE all ranges.
  const _italianRanges = [
    [10,199],[1010,1038],[1100,1100],[2010,2049],[2100,2100],
    [3010,3049],[3100,3100],[4010,4029],[4100,4100],[5010,5039],
    [5100,5100],[6010,6135],[7010,7100],[8010,8049],[8100,8100],
    [9010,9170],[10010,10156],[11010,11029],[11100,11100],[12010,12100],
    [13010,13100],[13800,13900],[14010,14100],[15010,15122],[16010,16167],
    [17010,17100],[18010,18100],[19010,19138],[20010,20162],[21010,21100],
    [22010,22100],[23010,23900],[24010,24129],[25010,25136],[26010,26100],
    [26801,26900],[27010,27100],[28010,28100],[28800,28925],[29010,29100],
    [30010,30176],[31010,31100],[32010,32100],[33010,33170],[34010,34170],
    [35010,35047],[35100,35100],[35121,35143],[36010,36100],[37010,37142],
    [38010,38123],[39010,39100],[40010,40141],[41010,41058],[41121,41126],
    [42010,42049],[42100,42124],[43010,43126],[44010,44124],[45010,45100],
    [46010,46100],[47010,47100],[47121,47122],[47521,47522],[47800,47924],
    [48010,48125],[50010,50145],[51010,51100],[52010,52100],[53010,53100],
    [54010,54100],[55010,55100],[56010,56128],[57010,57127],[58010,58100],
    [59010,59100],[60010,60131],[61010,61121],[62010,62100],[63010,63100],
    [63821,63900],[64010,64100],[65010,65129],[66010,66100],[67010,67100],
    [70010,70132],[71010,71122],[72010,72100],[73010,73100],[74010,74123],
    [75010,75100],[76010,76125],[80010,80147],[81010,81100],[82010,82100],
    [83010,83100],[84010,84135],[85010,85100],[86010,86170],[87010,87100],
    [88010,88100],[88811,88900],[89010,89135],[89811,89868],[89900,89900],
    [90010,90151],[91010,91100],[92010,92100],[93010,93100],[94010,94100],
    [95010,95131],[96010,96100],[97010,97100],[98010,98168],
  ];

  const hasShippingMismatch = () => {
    const currentRule = shippingRules.find(r => r.id === customerInfo.country);
    if (!currentRule) return false;
    const country = (customerInfo.specificCountry || "").trim().toLowerCase();
    const city = (customerInfo.city || "").trim().toLowerCase();
    const zipCode = (customerInfo.zipCode || "").trim();

    // ---- Universal zone-aware CAP validation ----
    // Each shipping zone has an expected CAP format. If the customer
    // selected "Italia" but typed a French CAP (or vice versa) → mismatch.
    const ruleId = (currentRule.id || "").toLowerCase();
    const ruleZone = (currentRule.zone || "").toLowerCase();
    const ruleCountry = (currentRule.country || "").toLowerCase();
    const zoneNorm =
      ruleZone === "national" || ruleZone === "nazionale" || ruleId === "italia" ? "italia"
      : ruleZone === "uk" || ruleId === "uk-shipping" ? "uk"
      : ruleZone === "netherlands" || ruleId === "netherlands-shipping" ? "netherlands"
      : ruleCountry.includes("francia") ? "francia"
      : ruleCountry.includes("germania") ? "germania"
      : ruleCountry.includes("spagna") ? "spagna"
      : ruleCountry.includes("belgio") ? "belgio"
      : ruleCountry.includes("austria") ? "austria"
      : ruleCountry.includes("svizzera") ? "svizzera"
      : ruleCountry.includes("portogallo") ? "portogallo"
      : ruleCountry.includes("polonia") ? "polonia"
      : ruleCountry.includes("stati uniti") || ruleZone === "usa" ? "usa"
      : ruleZone === "eu" || ruleCountry.includes("europa") ? "eu"
      : null;

    if (zoneNorm && zipCode) {
      const z = zipCode.trim().toUpperCase();
      const num = /^\d+$/.test(z) ? parseInt(z, 10) : null;
      const checks = {
        italia: () => /^\d{5}$/.test(z) && _italianRanges.some(([lo, hi]) => num >= lo && num <= hi),
        francia: () => /^\d{5}$/.test(z) && (
          (num >= 1000 && num <= 95999) || (num >= 97000 && num <= 98999)
        ),
        germania: () => /^\d{5}$/.test(z) && num >= 1067 && num <= 99998,
        spagna: () => /^\d{5}$/.test(z) && parseInt(z.substring(0,2), 10) >= 1 && parseInt(z.substring(0,2), 10) <= 52,
        uk: () => /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(z),
        netherlands: () => /^\d{4}\s?[A-Z]{2}$/.test(z),
        belgio: () => /^\d{4}$/.test(z) && num >= 1000 && num <= 9999,
        austria: () => /^\d{4}$/.test(z) && num >= 1010 && num <= 9999,
        svizzera: () => /^\d{4}$/.test(z) && num >= 1000 && num <= 9658,
        portogallo: () => /^\d{4}-\d{3}$/.test(z),
        polonia: () => /^\d{2}-\d{3}$/.test(z),
        usa: () => /^\d{5}(-\d{4})?$/.test(z),
        eu: () => (
          // any European format
          (/^\d{4,6}$/.test(z)) ||
          /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/.test(z) ||  // UK
          /^\d{4}\s?[A-Z]{2}$/.test(z) ||  // Netherlands
          /^\d{4}-\d{3}$/.test(z) || /^\d{2}-\d{3}$/.test(z) ||  // PT, PL
          /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/.test(z) ||  // Ireland Eircode (e.g. N39E4H9, A65 F4E2)
          /^[A-Z]{3}\s?\d{4}$/.test(z)  // Malta (e.g. VLT 1010)
        ),
      };
      const validator = checks[zoneNorm];
      if (validator && !validator()) {
        return true; // CAP doesn't match the selected zone → mismatch
      }
    }

    // Accept many synonyms for "Italian destination": country names + the
    // legacy "nazionale" label + ISO code.
    const italianNames = [
      "italia", "italy", "italie", "italien", "italian", "italiana", "it", "ita",
      "nazionale", "national", "naz",
    ];
    const isNational = zoneNorm === "italia";
    const isItalianText = italianNames.includes(country) || italianNames.some(n => country.includes(n));

    // Foreign country blacklist — explicit, much more reliable than trying to
    // whitelist every Italian comune (there are 7900+). If the customer types
    // any of these we know it's NOT an Italian shipment.
    const foreignCountries = [
      // EU
      "france","francia","francais","spain","spagna","españa","espana","germany","germania","deutschland",
      "belgium","belgio","belgique","austria","österreich","portugal","portogallo","portuguesa",
      "netherlands","olanda","paesi bassi","holland","poland","polonia","polska",
      "switzerland","svizzera","schweiz","sweden","svezia","sverige","norway","norvegia","norge",
      "denmark","danimarca","danmark","finland","finlandia","suomi","greece","grecia","ellada",
      "ireland","irlanda","éire","united kingdom","regno unito","inghilterra","britain","scotland","scozia","wales","galles",
      "czech","repubblica ceca","česká","hungary","ungheria","magyar","romania","bulgaria","croatia","croazia","hrvatska",
      "slovenia","slovakia","slovacchia","estonia","latvia","lettonia","lithuania","lituania",
      "luxembourg","lussemburgo","malta","cyprus","cipro",
      // Americas
      "usa","united states","stati uniti","america","canada","mexico","messico",
      "brazil","brasile","brasil","argentina","chile","cile","peru","perù","colombia","venezuela",
      "ecuador","uruguay","paraguay","bolivia",
      // Asia
      "china","cina","japan","giappone","india","korea","corea","hong kong",
      "thailand","thailandia","vietnam","singapore","indonesia","philippines","filippine","malaysia",
      // Oceania
      "australia","new zealand","nuova zelanda",
      // Other
      "russia","belarus","bielorussia","ukraine","ucraina","turkey","turchia",
      "south africa","sudafrica","israel","israele","uae","emirati","saudi","arabia",
      "egypt","egitto","morocco","marocco","tunisia","algeria",
    ];
    // Combined search across country + city: catches "Italia" in country
    // field but "Berlin" or "Henstedt Ulzburg" in city → still flagged.
    const combinedText = `${country} ${city}`.toLowerCase();
    const isForeignText = foreignCountries.some(c => combinedText.includes(c));

    // Multi-country city-name pattern detection: catches addresses where the
    // customer typed "Italia" but lives abroad. Heuristic: suffixes that don't
    // exist in Italian comuni + zip code shape. Covers Germany, France, UK,
    // Netherlands, Spain, Poland.
    const cityWords = city.split(/[\s,.()-]+/).filter(w => w.length >= 4);
    const zipStr = zipCode;
    const zipIs5Digits = /^\d{5}$/.test(zipStr);
    const zipHasLetter = /[a-zA-Z]/.test(zipStr);

    const germanSuffixes = ["burg","dorf","hausen","heim","stadt","bach","berg","feld","stein","ingen","kirchen","brück","haus","leben"];
    const ukSuffixes = ["ham","shire","ford","bury","borough","wick","chester","ster","mouth","minster","field","hill"];
    const frSubstrings = ["-sur-","-lès-","-les-","-en-","-aux","-sous-","-le-"];
    const cityLow = city;

    const looksGerman = zipIs5Digits && cityWords.some(w => germanSuffixes.some(s => w.endsWith(s)));
    const looksUK = zipHasLetter && /\d/.test(zipStr); // UK postcodes mix letters+digits
    const looksUKBySuffix = zipHasLetter && cityWords.some(w => ukSuffixes.some(s => w.endsWith(s)));
    const looksFrench = zipIs5Digits && frSubstrings.some(s => cityLow.includes(s));
    const looksDutch = zipHasLetter && (cityLow.includes("dam") || cityLow.includes("veen") || cityLow.includes("haag") || cityLow.includes("broek"));
    const looksPolish = /^\d{2}-\d{3}$/.test(zipStr);

    // Foreign capital-city blacklist (Bruz, Berlin, Madrid, Paris, …) —
    // catches addresses where city/zip CAP overlaps an Italian range.
    const foreignCitiesShort = [
      "bruz","rennes","paris","lyon","marseille","toulouse","nice","nantes",
      "strasbourg","bordeaux","lille",
      "berlin","munich","munchen","hamburg","frankfurt","stuttgart","koln",
      "henstedt","ulzburg","leipzig","dresden","hannover",
      "madrid","barcelona","sevilla","valencia","zaragoza",
      "london","manchester","liverpool","birmingham","glasgow","edinburgh",
      "amsterdam","rotterdam","utrecht","eindhoven",
      "wien","vienna","graz","brussels","bruxelles","antwerp",
      "warszawa","warsaw","krakow","praha","prague","athens","stockholm",
      "copenhagen","helsinki","oslo","zurich","geneva","bern","basel",
    ];
    const looksForeignCity = foreignCitiesShort.some(c => cityLow.includes(c));

    // Italian CAP whitelist already defined at top via _italianRanges
    let zipNotItalian = false;
    if (zipIs5Digits) {
      const z = parseInt(zipStr, 10);
      zipNotItalian = !_italianRanges.some(([lo, hi]) => z >= lo && z <= hi);
    }

    const looksForeign = looksGerman || looksUK || looksUKBySuffix || looksFrench || looksDutch || looksPolish || looksForeignCity || zipNotItalian;

    // Block: national zone selected but explicitly foreign country typed
    if (isNational && isForeignText) return true;

    // Block: national zone selected but city/zip pattern looks foreign
    if (isNational && looksForeign) return true;

    // Block: foreign zone selected but Italian text typed
    if (!isNational && isItalianText && !looksForeign) return true;

    // Everything else (Italian cities, comuni, "via XYZ", etc) passes through
    return false;
  };

  const handleCheckout = async () => {
    if (cart.length === 0) {
      alert("Il carrello è vuoto!");
      return;
    }
    // Reset any previous checkout state
    resetCheckoutState();
    setShowPaymentOptions(true);
  };

  // 🛒 Recupero carrello: appena l'utente compila un'email valida al checkout,
  // traccia il carrello (debounce 2s). Il backend invia l'email di recupero
  // dopo 1h se l'ordine non viene completato.
  useEffect(() => {
    const email = (customerInfo.email || "").trim().toLowerCase();
    if (!email || !email.includes("@") || email.length < 6 || cart.length === 0) return;
    const t = setTimeout(() => {
      axios.post(`${API}/cart/track`, {
        email,
        full_name: customerInfo.name || "",
        cart_items: cart.map((i) => ({
          article_id: i.article_id,
          title: i.article?.title || "",
          quantity: i.quantity,
          price: i.article?.price || 0,
        })),
        cart_total: getCartTotal(),
      }).catch(() => {});
    }, 2000);
    return () => clearTimeout(t);
  }, [customerInfo.email, customerInfo.name, cart]);

  const handlePaymentSelection = async (paymentMethod) => {
    setSelectedPaymentMethod(paymentMethod);
    
    // Don't process checkout immediately - let user fill customer info form first
    // Checkout will be triggered when user clicks the confirmation button after filling the form
  };

  // Build the checkout payload — shared by Stripe Checkout (full flow) and
  // Express Checkout (Apple Pay / Google Pay direct).
  const buildExpressPayload = () => ({
    items: cart.map((item) => ({ article_id: item.article_id, quantity: item.quantity })),
    customer_email: customerInfo.email || "cliente@example.com",
    customer_name: customerInfo.name || "Cliente",
    customer_country: customerInfo.country || "italia",
    customer_specific_country: customerInfo.specificCountry || "",
    shipping_method: selectedShippingMethod,
    shipping_cost: getShippingTotal(),
    inpost_pickup_point: selectedShippingMethod === "inpost" ? inpostPickupPoint : null,
    inpost_insurance: selectedShippingMethod === "inpost" ? inpostInsurance : false,
    coupon_code: appliedCoupon ? appliedCoupon.code : null,
    discount_amount: appliedCoupon ? getDiscountAmount() : 0,
    shipping_address: {
      phone: customerInfo.phone || "",
      address: selectedShippingMethod === "inpost" ? `InPost: ${inpostPickupPoint}` : (customerInfo.address || ""),
      city: customerInfo.city || "",
      postal_code: customerInfo.zipCode || "",
      country: customerInfo.country || "italia",
    },
  });

  const processPayPalCheckout = async () => {
    try {
      if (selectedShippingMethod === "inpost" && !inpostPickupPoint.trim()) {
        alert("Per favore inserisci il codice o l'indirizzo del punto di ritiro InPost!");
        return;
      }
      if (!customerInfo.specificCountry || !customerInfo.specificCountry.trim()) {
        alert("Per favore inserisci il Paese di destinazione!");
        return;
      }
      setIsCheckingOut(true);
      const payload = buildExpressPayload();
      const { data } = await axios.post(`${API}/paypal/create-order`, payload);
      if (data?.approval_url) {
        window.location.href = data.approval_url;
      } else {
        throw new Error("URL PayPal mancante nella risposta");
      }
    } catch (e) {
      alert("❌ " + (e?.response?.data?.detail || e.message || "Errore PayPal"));
      setIsCheckingOut(false);
    }
  };

  const processStripeCheckout = async () => {
    try {
      // Validate InPost pickup point if InPost is selected
      if (selectedShippingMethod === "inpost" && !inpostPickupPoint.trim()) {
        alert("Per favore inserisci il codice o l'indirizzo del punto di ritiro InPost!");
        return;
      }
      // Validate specific country
      if (!customerInfo.specificCountry || !customerInfo.specificCountry.trim()) {
        alert("Per favore inserisci il Paese di destinazione!");
        return;
      }
      setIsCheckingOut(true);
      const checkoutData = buildExpressPayload();
      const response = await axios.post(`${API}/checkout/session`, checkoutData);
      
      // Redirect to Stripe checkout
      if (response.data.url) {
        window.location.href = response.data.url;
        // Note: State won't be reset here because page redirects
      } else {
        throw new Error("URL di checkout non ricevuto dal server");
      }

    } catch (err) {
      console.error("Error during checkout:", err);
      // Surface the backend's detail message so the customer knows WHY
      // (e.g. "Il paese 'NAZIONALE' non corrisponde alla zona Italia").
      // Fall back to the generic message only if no detail is available.
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        err?.message;
      const msg = detail && typeof detail === "string" && detail.length > 4
        ? `❌ Errore: ${detail}\n\nVerifica i dati e riprova.`
        : "Errore durante il checkout. Riprova.";
      alert(msg);
      setIsCheckingOut(false); // Reset state on error
    }
    // Finally block removed since redirect prevents execution
  };

  // PayPal removed on 28 May 2026 (merchant account closed).
  // Historical orders with payment_method="paypal" still display correctly
  // via the label maps elsewhere, but no new PayPal flow is initiated.

  const handleOrderSubmit = async () => {
    //console.log("🔥 handleOrderSubmit called", { customerInfo, selectedPaymentMethod, cart });

    // Validate cart data
    if (!cart || cart.length === 0) {
      setOrderConfirmationMessage("Il carrello è vuoto!");
      setShowOrderConfirmation(true);
      return;
    }
    
    // Check for corrupted cart items
    const validCart = cart.filter(item => item && item.article && item.article.price);
    if (validCart.length !== cart.length) {
      console.warn("Found corrupted cart items, filtering them out");
      setCart(validCart);
    }
    
    // Detailed validation with specific missing fields
    const missingFields = [];
    if (!customerInfo.name) missingFields.push("Nome completo");
    if (!customerInfo.email) missingFields.push("Email");
    if (!customerInfo.phone) missingFields.push("Telefono");
    if (!customerInfo.address) missingFields.push("Indirizzo di spedizione");
    if (!customerInfo.city) missingFields.push("Città");
    if (!customerInfo.specificCountry || !customerInfo.specificCountry.trim()) missingFields.push("Paese / Country");
    
    if (missingFields.length > 0) {
      const errorMessage = `❌ CAMPI OBBLIGATORI MANCANTI:\n\n${missingFields.map(field => `• ${field}`).join('\n')}\n\n⚠️ Compila tutti i campi per procedere con l'ordine!`;
      setOrderConfirmationMessage(errorMessage);
      setShowOrderConfirmation(true);
      return;
    }

    try {
      setIsCheckingOut(true);
      //console.log("🔄 Processing order...");
      
      // Create customer account if requested
      if (createAccount && accountPassword) {
        try {
          //console.log("👤 Creating customer account...");
          await axios.post(`${API}/auth/register`, {
            username: customerInfo.email,
            email: customerInfo.email,
            password: accountPassword,
            full_name: customerInfo.name,
            phone: customerInfo.phone,
            role: "customer"
          });
          //console.log("✅ Customer account created successfully");
        } catch (registerError) {
          console.warn("⚠️ Account creation failed (user may already exist):", registerError);
          // Continue with order even if registration fails (user might already exist)
        }
      }

      // Safe calculation of total
      let totalAmount = 0;
      try {
        totalAmount = getCartTotal();
        //console.log("💰 Total amount calculated:", totalAmount);
      } catch (calcError) {
        console.error("Error calculating total:", calcError);
        setOrderConfirmationMessage("Errore nel calcolo del totale. Riprova.");
        setShowOrderConfirmation(true);
        setIsCheckingOut(false);
        return;
      }

      // Link order to logged-in customer (if any)
      const loggedInUser = (() => {
        try {
          const token = sessionStorage.getItem('access_token');
          if (!token) return null;
          const payload = JSON.parse(atob(token.split('.')[1]));
          return payload?.sub || null;
        } catch { return null; }
      })();

      const orderData = {
        items: validCart,
        customer_info: customerInfo,
        payment_method: selectedPaymentMethod,
        shipping_cost: getShippingTotal(),
        shipping_method: "standard",
        total_amount: totalAmount,
        order_date: new Date().toISOString(),
        status: "pending",
        coupon_code: appliedCoupon ? appliedCoupon.code : null,
        discount_amount: appliedCoupon ? getDiscountAmount() : 0,
        user_id: loggedInUser
      };

      //console.log("📦 Order data:", orderData);

      // Send order to backend
      const response = await axios.post(`${API}/orders`, orderData);

      //console.log("✅ Order response:", response.data);

      // ----- Satispay: redirect automatic flow -----
      // After the order is persisted, immediately create a Satispay payment
      // and redirect the customer to Satispay's QR/checkout page. The order
      // stays in `awaiting_payment` until the Satispay webhook fires `ACCEPTED`.
      if (selectedPaymentMethod === "satispay") {
        const orderId = response.data.order_id || response.data.id;
        const orderRef = `#${(orderId || "").slice(-6)}`;
        try {
          const amountCents = Math.round(totalAmount * 100);
          const satispayResp = await axios.post(`${API}/satispay/create-payment`, {
            amount_cents: amountCents,
            external_code: orderId,
            description: `Edicola Cerasuolo - Ordine ${orderRef}`,
          });
          const redirectUrl = satispayResp.data?.redirect_url;
          if (redirectUrl) {
            // Don't clear the cart yet — only clear once webhook fires.
            try { localStorage.setItem("pendingSatispayOrderId", orderId); } catch (_) { /* ignore */ }
            window.location.href = redirectUrl;
            return; // hard stop — page is navigating away
          }
          // No redirect URL → treat as failure, show explicit error.
          console.warn("Satispay: redirect_url missing in response");
        } catch (satispayErr) {
          console.error("Satispay create-payment failed:", satispayErr);
        }
        // ⚠️ Reached only if Satispay setup failed: show explicit error,
        // DO NOT pretend the order was confirmed, DO NOT clear the cart.
        setOrderConfirmationMessage(
          `⚠️ PAGAMENTO SATISPAY NON DISPONIBILE\n\n` +
          `Si è verificato un problema temporaneo con il sistema di pagamento Satispay.\n\n` +
          `Il tuo ordine è stato salvato come PRENOTAZIONE (NON confermato, NON pagato).\n` +
          `Riferimento: ${orderRef}\n\n` +
          `Cosa puoi fare:\n` +
          `1) Riprova tra qualche minuto\n` +
          `2) Scegli un altro metodo di pagamento\n` +
          `3) Contattaci a amm@edicolavillaricca.com con il riferimento sopra\n\n` +
          `Importo: €${totalAmount.toFixed(2)}`
        );
        setShowOrderConfirmation(true);
        setIsCheckingOut(false);
        return; // 🛑 hard stop — no fake success, no cart clear
      }

      // Show success message based on payment method
      let message = "";
      const _tgOrderId = response.data.order_id || response.data.id;
      setOrderTelegramLink(_tgOrderId ? `https://t.me/Edicolavillariccabot?start=o_${_tgOrderId}` : "");

      switch (selectedPaymentMethod) {
        case "cash":
          message = `✅ ORDINE RICEVUTO!\n\nTi contatteremo al ${customerInfo.phone} per concordare la consegna con pagamento in contanti.\n\nTotale: €${totalAmount.toFixed(2)}\n\n📧 Riceverai una email di conferma con tutti i dettagli!`;
          break;
        case "satispay":
          message = `✅ ORDINE RICEVUTO!\n\n🟥 PAGAMENTO CON SATISPAY\n\nTi invieremo le istruzioni per completare il pagamento Satispay all'indirizzo: ${customerInfo.email}\n\nTotale: €${totalAmount.toFixed(2)}\n\nPagamento mobile veloce e sicuro con la tua app Satispay.\n\n📧 Riceverai una email di conferma con tutti i dettagli!`;
          break;
        case "transfer":
          message = `✅ ORDINE RICEVUTO!\n\n💶 DATI PER IL BONIFICO:\n\n📋 Intestatario: Massimiliano Cerasuolo\n\n🏦 OPZIONE 1 - IBAN Italiano:\nIBAN: IT36I0306939952100000066106\n\n🏦 OPZIONE 2 - IBAN Internazionale:\nIBAN: IE67SUMU99036510351260\nBIC/SWIFT: SUMUIE22XXX\nBanca: SumUp Limited\n\n📝 Causale: Ordine #${response.data.order_id.slice(-6)}\n💰 Importo: €${totalAmount.toFixed(2)}\n\n📧 Dopo il bonifico, invia la ricevuta a:\namm@edicolavillaricca.com\n\n✉️ Riceverai conferma via email con tutti i dettagli!`;
          break;
        case "klarna":
          message = `✅ ORDINE RICEVUTO!\n\n🔷 PAGAMENTO CON KLARNA\n\nTi invieremo le istruzioni per completare il pagamento Klarna all'indirizzo: ${customerInfo.email}\n\nTotale: €${totalAmount.toFixed(2)}\n\nPotrai pagare dopo 30 giorni o in 3 rate senza interessi.\n\n📧 Riceverai una email di conferma con tutti i dettagli!`;
          break;
        default:
          message = `✅ ORDINE RICEVUTO!\n\nTi ricontatteremo presto.\n\nTotale: €${totalAmount.toFixed(2)}\n\n📧 Riceverai una email di conferma con tutti i dettagli!`;
      }

      setOrderConfirmationMessage(message);
      setShowOrderConfirmation(true);
      //console.log("🎉 Showing confirmation message");

      // Clear cart and form after a longer delay
      setTimeout(() => {
        setCart([]);
        setIsCartOpen(false);
        setShowPaymentOptions(false);
        setSelectedPaymentMethod("");
        setCustomerInfo({
          name: "", email: "", phone: "", address: "", city: "", zipCode: "", notes: "", country: "italia", specificCountry: ""
        });
        setShowOrderConfirmation(false);
      }, 30000); // 30 seconds to read the message

    } catch (error) {
      console.error("❌ Error submitting order:", error);
      const errorMsg = `Errore nell'invio dell'ordine:\n\n${error.response?.data?.detail || error.message || 'Errore sconosciuto'}\n\nRiprova tra qualche minuto o contattaci al +39 347 080 8245`;
      setOrderConfirmationMessage(errorMsg);
      setShowOrderConfirmation(true);
    } finally {
      //console.log("🔧 Setting isCheckingOut to false");
      setIsCheckingOut(false);
    }
  };

  const categorie = useMemo(() => [...new Set(articles.map(article => article.category).filter(Boolean))], [articles]);
  const articoliCategoria = useMemo(() => filtroCategoria === "all" 
    ? articles 
    : filtroCategoria === "prevendita"
    ? articles.filter(article => article.availability_type === 'preorder')
    : filtroCategoria === "prenotazione"
    ? articles.filter(article => article.availability_type === 'reservation')
    : articles.filter(article => article.category === filtroCategoria), [articles, filtroCategoria]);

  // Ricerca full-text client-side su titolo, descrizione, categoria e SKU
  const articoliRicerca = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return articoliCategoria;
    return articoliCategoria.filter((a) =>
      `${a.title || ""} ${a.description || ""} ${a.category || ""} ${a.sku || ""}`.toLowerCase().includes(q)
    );
  }, [articoliCategoria, searchQuery]);

  // Ordinamento: rilevanza (default) | prezzo asc/desc (IVA inclusa) | novità
  const articoliFiltrati = useMemo(() => {
    const priceIva = (a) => (a.price || 0) * (1 + parseFloat(a.iva_percentage || 0) / 100);
    const arr = [...articoliRicerca];
    if (sortBy === "price-asc") arr.sort((a, b) => priceIva(a) - priceIva(b));
    else if (sortBy === "price-desc") arr.sort((a, b) => priceIva(b) - priceIva(a));
    else if (sortBy === "newest") arr.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    else if (sortBy === "rating") {
      const score = (a) => {
        const r = ratingsByArticle[a.id];
        return r ? r.sum / r.count + Math.min(r.count, 10) * 0.05 : -1;
      };
      arr.sort((a, b) => score(b) - score(a));
    }
    return arr;
  }, [articoliRicerca, sortBy, ratingsByArticle]);

  // Focused article for deep-linked SEO (?article=ID) + dynamic title/Schema.org
  const focusedArticle = useMemo(
    () => (articleFocusId ? articles.find((a) => a.id === articleFocusId) : null),
    [articleFocusId, articles]
  );

  // Scroll focused article into view once articles are loaded
  useEffect(() => {
    if (!focusedArticle) return;
    const el = document.getElementById(`product-card-${focusedArticle.id}`);
    if (el) {
      setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 150);
    }
  }, [focusedArticle]);

  // SEO: when /prodotti?article={uuid} resolves to an article WITH a slug,
  // do a client-side replaceState to the canonical URL /prodotto/{slug}.
  // This signals to Google the real URL of the resource (the legacy
  // ?article=UUID URLs are still visible to old visitors but bots get
  // the proper slug + canonical tag below).
  useEffect(() => {
    if (focusedArticle && focusedArticle.slug && typeof window !== "undefined") {
      const target = `/prodotto/${focusedArticle.slug}`;
      if (window.location.pathname !== target) {
        try {
          window.history.replaceState({}, "", target);
        } catch (_) { /* no-op */ }
      }
    }
  }, [focusedArticle]);

  // Schema.org Product JSON-LD for the focused article (or generic Product list otherwise)
  const productSchema = useMemo(() => {
    if (!focusedArticle) return null;
    const a = focusedArticle;
    const priceWithIva = (a.price * (1 + (parseFloat(a.iva_percentage) || 0) / 100)).toFixed(2);
    const inStock = (a.quantity_stock || 0) > 0;
    const isPreorder = a.availability_type === 'preorder';
    const image = a.images_url?.[0] || (a.photo_url ? `${BACKEND_URL}${a.photo_url}` : null);
    return {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": a.title,
      "description": a.description || a.title,
      ...(image && { "image": image }),
      ...(a.category && { "category": a.category }),
      "brand": { "@type": "Brand", "name": a.brand || a.category || "Edicola Cerasuolo" },
      "sku": a.id,
      "offers": {
        "@type": "Offer",
        "url": `https://edicolavillaricca.com/prodotti?article=${a.id}`,
        "priceCurrency": "EUR",
        "price": priceWithIva,
        "availability": isPreorder
          ? "https://schema.org/PreOrder"
          : inStock
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        "seller": { "@type": "Organization", "name": "Edicola Cerasuolo" }
      }
    };
  }, [focusedArticle]);

  // Schema.org ItemList for the listing (when no specific article is focused)
  const itemListSchema = useMemo(() => {
    if (focusedArticle || articoliFiltrati.length === 0) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": articoliFiltrati.slice(0, 30).map((a, idx) => ({
        "@type": "ListItem",
        "position": idx + 1,
        "url": `https://edicolavillaricca.com/prodotti?article=${a.id}`,
        "name": a.title
      }))
    };
  }, [focusedArticle, articoliFiltrati]);

  // Map between category names and SEO-friendly /collezione/{slug} URLs
  // Used so that /prodotti?filter=X canonical points to the indexed collection page
  const FILTER_TO_COLLECTION_SLUG = {
    "Truck Tuning Collection": "truck-tuning",
    "Racing Car Transporters": "racing-car-transporters",
    "Pokemon": "pokemon-tcg",
    "Auto da corsa": "auto-da-corsa-f1",
  };

  // Dynamic page metadata
  const pageMeta = useMemo(() => {
    if (focusedArticle) {
      const a = focusedArticle;
      const priceWithIva = (a.price * (1 + (parseFloat(a.iva_percentage) || 0) / 100)).toFixed(2);
      // SEO: canonical points to /prodotto/{slug} when available so Google
      // associates the legacy ?article=UUID URLs with the slug-based ones.
      const canonical = a.slug
        ? `https://edicolavillaricca.com/prodotto/${a.slug}`
        : "https://edicolavillaricca.com/prodotti";
      return {
        title: `${a.title} — ${priceWithIva}€ | Edicola Cerasuolo`,
        description: (a.description || a.title).slice(0, 158),
        canonical,
      };
    }
    if (filtroCategoria && filtroCategoria !== 'all') {
      const collectionSlug = FILTER_TO_COLLECTION_SLUG[filtroCategoria];
      // If we have a dedicated /collezione/{slug} page, canonical points there
      const canonical = collectionSlug
        ? `https://edicolavillaricca.com/collezione/${collectionSlug}`
        : "https://edicolavillaricca.com/prodotti";
      return {
        title: `${filtroCategoria} — Catalogo | Edicola Cerasuolo`,
        description: `Sfoglia tutti i prodotti della categoria ${filtroCategoria}. Spedizioni internazionali assicurate. Pagamenti sicuri Stripe, Klarna.`,
        canonical,
      };
    }
    return {
      title: "Catalogo Prodotti — Modellismo, Pokemon TCG, Centauria | Edicola Cerasuolo",
      description: "Catalogo completo Edicola Cerasuolo: modellini F1 Ferrari LookSmart 1:43, carte Pokemon TCG, Racing Car Transporters Centauria, Truck Tuning Collection. Spedizioni internazionali.",
      canonical: "https://edicolavillaricca.com/prodotti"
    };
  }, [focusedArticle, filtroCategoria]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f]">
        <Navigation />
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-pink-500 mx-auto mb-4"></div>
            <p className="text-gray-400">Caricamento prodotti...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* React 19 native head tags — dynamic per product / category */}
      <title>{pageMeta.title}</title>
      <meta name="description" content={pageMeta.description} />
      <link rel="canonical" href={pageMeta.canonical} />
      <meta property="og:title" content={pageMeta.title} />
      <meta property="og:description" content={pageMeta.description} />
      <meta property="og:type" content={focusedArticle ? "product" : "website"} />
      {focusedArticle && (focusedArticle.images_url?.[0] || focusedArticle.photo_url) && (
        <meta property="og:image" content={focusedArticle.images_url?.[0] || `${BACKEND_URL}${focusedArticle.photo_url}`} />
      )}
      {productSchema && (
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      )}
      {itemListSchema && (
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      )}

      <Navigation />
      
      {/* Wishlist toast */}
      {wishlistMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] bg-amber-500 text-black px-4 py-2 rounded-lg shadow-lg text-sm font-medium" data-testid="wishlist-toast">
          {wishlistMsg}
        </div>
      )}

      {/* Cart Notification */}
      {getCartItemsCount() > 0 && (
        <div className="fixed top-20 right-4 z-50">
          <Button
            onClick={() => setIsCartOpen(true)}
            className="bg-green-600 hover:bg-green-700 rounded-full p-3 shadow-lg"
            data-testid="cart-toggle"
          >
            🛒 {getCartItemsCount()}
          </Button>
        </div>
      )}

      {/* Order Confirmation Modal */}
      <OrderConfirmationModal
        open={showOrderConfirmation}
        message={orderConfirmationMessage}
        telegramLink={orderTelegramLink}
        onClose={() => {
          setShowOrderConfirmation(false);
          setCart([]);
          setIsCartOpen(false);
          setShowPaymentOptions(false);
          setSelectedPaymentMethod("");
          setCustomerInfo({
            name: "", email: "", phone: "", address: "", city: "", zipCode: "", notes: "", country: "italia", specificCountry: ""
          });
          window.location.href = "/";
        }}
      />

      {/* Cart Modal */}
      <CartModal
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        getCartItemsCount={getCartItemsCount}
        updateCartQuantity={updateCartQuantity}
        removeFromCart={removeFromCart}
        getCartItemsTotal={getCartItemsTotal}
        getCartTotal={getCartTotal}
        getShippingTotal={getShippingTotal}
        shippingRules={shippingRules}
        shippingRulesNoInpost={shippingRulesNoInpost}
        customerInfo={customerInfo}
        setCustomerInfo={setCustomerInfo}
        appliedCoupon={appliedCoupon}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        couponError={couponError}
        setCouponError={setCouponError}
        couponLoading={couponLoading}
        applyCoupon={applyCoupon}
        removeCoupon={removeCoupon}
        getDiscountAmount={getDiscountAmount}
        showPaymentOptions={showPaymentOptions}
        setShowPaymentOptions={setShowPaymentOptions}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        handleCheckout={handleCheckout}
        handlePaymentSelection={handlePaymentSelection}
        handleOrderSubmit={handleOrderSubmit}
        processStripeCheckout={processStripeCheckout}
        processPayPalCheckout={processPayPalCheckout}
        buildExpressPayload={buildExpressPayload}
        isCheckingOut={isCheckingOut}
        setIsCheckingOut={setIsCheckingOut}
        hasShippingMismatch={hasShippingMismatch}
        createAccount={createAccount}
        setCreateAccount={setCreateAccount}
        accountPassword={accountPassword}
        setAccountPassword={setAccountPassword}
        resetCheckoutState={resetCheckoutState}
      />

      {/* Hero Banner Top — dark, in linea con la home */}
      <div className="relative w-full overflow-hidden bg-[#0a0a0f]" data-testid="catalog-hero">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/daba7cd3-bfb2-40c7-9ccb-537c58cf24ca/images/4cc8b9642e94a539110929da4435e72003dcce97cda60526174254ec3c032bff.jpeg"
          alt="Catalogo modellini Edicola Cerasuolo"
          className="w-full h-[250px] md:h-[300px] object-cover object-center opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/30 to-transparent"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 w-full">
            <p className="text-pink-500 text-[11px] font-bold uppercase tracking-[0.3em] mb-2">Edicola Cerasuolo</p>
            <h1 className="text-4xl sm:text-5xl font-extrabold uppercase text-white leading-none">
              Catalogo <span className="text-pink-500">Prodotti</span>
            </h1>
            <p className="text-gray-400 text-sm mt-3 max-w-md">Modellini, camion d'epoca, F1 e Pokémon TCG. Originali, spediti con cura in tutta Europa.</p>
          </div>
        </div>
      </div>

      {/* Promotional Banner - Products Section */}
      {promoBanners.length > 0 && (
        <div className="bg-gradient-to-r from-pink-600 to-fuchsia-500 py-3 px-4 shadow-md" data-testid="promo-banner-products">
          <div className="max-w-7xl mx-auto text-center">
            {promoBanners.map((b) => (
              <p key={b.code} className="text-sm md:text-base font-bold text-white tracking-wide">
                {b.banner_text} {b.discount_type === "percentage" ? `(-${b.discount_value}%)` : `(-€${b.discount_value})`} — Codice: <span className="bg-white/30 px-2 py-0.5 rounded font-mono">{b.code}</span>
              </p>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 pt-8">
        
        {error && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <Package className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-blue-900">Catalogo in Aggiornamento</h3>
                <p className="text-blue-700">{error}</p>
                <p className="text-blue-600 mt-2">
                  📞 Chiamaci al <strong>+39 347 080 8245</strong> per informazioni sui prodotti disponibili
                </p>
              </div>
            </div>
          </div>
        )}

        {articles.length > 0 && (
          <>
            {/* Barra di ricerca + ordinamento */}
            <div className="mb-6 flex flex-col md:flex-row md:items-start gap-3" data-testid="catalog-search-wrapper">
              <div className="relative flex-1 max-w-xl">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-lg pointer-events-none">🔍</span>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca un modellino, una carta, uno SKU..."
                  className="w-full pl-12 pr-12 py-3.5 bg-[#12121a] border border-gray-700 focus:border-pink-500 rounded-full text-white placeholder-gray-500 text-sm outline-none transition-colors"
                  data-testid="catalog-search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    aria-label="Cancella ricerca"
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-gray-700 hover:bg-pink-600 text-white text-xs flex items-center justify-center transition-colors"
                    data-testid="catalog-search-clear"
                  >
                    ×
                  </button>
                )}
                {searchQuery.trim() && (
                  <p className="mt-2 ml-4 text-xs text-gray-500" data-testid="catalog-search-count">
                    {articoliFiltrati.length} {articoliFiltrati.length === 1 ? "risultato" : "risultati"} per "{searchQuery.trim()}"
                  </p>
                )}
              </div>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-5 py-3.5 bg-[#12121a] border border-gray-700 focus:border-pink-500 rounded-full text-white text-sm outline-none cursor-pointer transition-colors md:w-auto"
                data-testid="catalog-sort-select"
                aria-label="Ordina prodotti"
              >
                <option value="default">Ordina: Rilevanza</option>
                <option value="newest">Novità (più recenti)</option>
                <option value="price-asc">Prezzo: crescente</option>
                <option value="price-desc">Prezzo: decrescente</option>
                <option value="rating">Più recensiti / valutati ★</option>
              </select>
            </div>

            {/* Filtri Categoria */}
            <CategoryFilters
              categorie={categorie}
              articles={articles}
              current={filtroCategoria}
              onChange={setFiltroCategoria}
            />

            {/* Griglia Prodotti */}
            {articoliFiltrati.length === 0 ? (
              <div className="col-span-full py-16 text-center">
                <div className="text-6xl mb-4">📦</div>
                <h3 className="text-xl font-semibold text-white mb-2">
                  Nessun prodotto trovato
                </h3>
                <p className="text-gray-400">
                  {filtroCategoria === "prevendita" ? (
                    "Al momento non ci sono prodotti in prevendita."
                  ) : filtroCategoria === "prenotazione" ? (
                    "Al momento non ci sono prodotti in prenotazione."
                  ) : (
                    "Nessun prodotto corrisponde ai criteri di ricerca."
                  )}
                </p>
                {filtroCategoria !== "all" && (
                  <Button
                    onClick={() => setFiltroCategoria("all")}
                    className="mt-4"
                  >
                    Vedi Tutti i Prodotti
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {articoliFiltrati.map((article) => (
                <ProductCard
                  key={article.id}
                  article={article}
                  discount={getArticleDiscount(article.id)}
                  badges={articleBadges[article.id]}
                  wishlistCount={wishlistCounts[article.id] || 0}
                  wishlistIds={wishlistIds}
                  isFocused={focusedArticle?.id === article.id}
                  onZoomImage={setLightbox}
                  onAddToCart={addToCart}
                  onToggleWishlist={toggleWishlist}
                  reviews={reviews}
                  showReviewForm={showReviewForm}
                  reviewForm={reviewForm}
                  setReviewForm={setReviewForm}
                  onToggleReviewForm={(id) => {
                    setShowReviewForm(prev => ({ ...prev, [id]: !prev[id] }));
                    if (!reviews[id]) {
                      loadReviews(id);
                    }
                  }}
                  onSubmitReview={submitReview}
                />
              ))}
              </div>
            )}
          </>
        )}

        {/* Sezione vuota con prodotti di esempio */}
        {articles.length === 0 && !error && (
          <div className="text-center py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
              {/* Prodotti di esempio */}
              <Card className="hover:shadow-lg transition-shadow">
                <div className="aspect-square w-full bg-gradient-to-br from-red-400 to-red-600 rounded-t-lg flex items-center justify-center">
                  <div className="text-6xl">🏎️</div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">Ferrari F1 2024</CardTitle>
                  <Badge variant="secondary">Modellismo F1</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-3">Modello ufficiale Ferrari Formula 1 stagione 2024</p>
                  <div className="font-bold text-green-600 mb-3">€29,99</div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '/contatto'}>
                    Richiedi Info
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <div className="aspect-square w-full bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-t-lg flex items-center justify-center">
                  <div className="text-6xl">🎴</div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">Pokémon Mystery Box</CardTitle>
                  <Badge variant="secondary">Pokémon TCG</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-3">Box sorpresa con carte rare e speciali</p>
                  <div className="font-bold text-green-600 mb-3">€15,99</div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '/contatto'}>
                    Richiedi Info
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <div className="aspect-square w-full bg-gradient-to-br from-blue-400 to-blue-600 rounded-t-lg flex items-center justify-center">
                  <div className="text-6xl">🏆</div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">Casco Leclerc 2024</CardTitle>
                  <Badge variant="secondary">Caschi Ferrari</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-3">Replica casco Charles Leclerc stagione 2024</p>
                  <div className="font-bold text-green-600 mb-3">€89,99</div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '/contatto'}>
                    Richiedi Info
                  </Button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <div className="aspect-square w-full bg-gradient-to-br from-gray-400 to-gray-600 rounded-t-lg flex items-center justify-center">
                  <div className="text-6xl">🚛</div>
                </div>
                <CardHeader>
                  <CardTitle className="text-lg">Camion Vintage</CardTitle>
                  <Badge variant="secondary">Camion d'Epoca</Badge>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 text-sm mb-3">Modello storico camion anni '70</p>
                  <div className="font-bold text-green-600 mb-3">€45,99</div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => window.location.href = '/contatto'}>
                    Richiedi Info
                  </Button>
                </CardContent>
              </Card>
            </div>

            <div className="text-center">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Catalogo in Aggiornamento</h3>
              <p className="text-gray-600 mb-6">
                Stiamo caricando il nostro catalogo completo. Nel frattempo, contattaci per informazioni sui prodotti disponibili!
              </p>
              <Button 
                className="bg-blue-600 hover:bg-blue-700 mr-4"
                onClick={() => window.location.href = '/contatto'}
              >
                Contattaci
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = 'tel:+393470808245'}
              >
                📞 Chiamaci
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Banner Bottom — dark CTA */}
      <div className="relative w-full overflow-hidden bg-[#0a0a0f] border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-white font-extrabold uppercase text-xl md:text-2xl">Non trovi quello che cerchi?</h2>
            <p className="text-gray-500 text-sm mt-1">Scrivici: possiamo procurarti uscite passate, abbonamenti e pezzi rari.</p>
          </div>
          <a href="https://wa.me/393470808245?text=Ciao!%20Sto%20cercando%20un%20prodotto%20particolare" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-3 bg-pink-600 hover:bg-pink-500 text-white font-extrabold uppercase text-xs tracking-wide skew-x-[-6deg] transition-colors" data-testid="catalog-bottom-cta">
            <span className="skew-x-[6deg]">💬 Chiedici su WhatsApp →</span>
          </a>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-[#08080d] border-t border-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-400 mb-4">
            © 2024 Edicola Cerasuolo - Corso Europa 175, 80010 Villaricca (NA)
          </p>
          <p className="text-gray-400">
            Per informazioni sui prodotti: 📞 +39 347 080 8245 | 📧 amm@edicolavillaricca.com
          </p>
        </div>
      </footer>

      {/* Lightbox: full-size product image on click */}
      <Lightbox lightbox={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
};
